import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useHttp } from '@climate/components'
import type { Entity, EntityUpdatePayload, MemberAgreementResponse } from '../types'

const ENDPOINT = '/api/v1/entities'

export function useEntitiesService(entityId?: string) {
  const queryClient = useQueryClient()
  const http = useHttp()

  const { data: entity } = useQuery({
    queryKey: ['entity', entityId],
    queryFn: (): Promise<Entity> => http.apiGet(`${ENDPOINT}/${entityId}`) as Promise<Entity>,
    enabled: !!entityId,
  })

  const updateEntityMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: EntityUpdatePayload }) =>
      http.apiPut(`${ENDPOINT}/${id}`, payload as Record<string, unknown>) as Promise<Entity>,
    onSuccess: (data: Entity, { id }: { id: string; payload: EntityUpdatePayload }) => {
      queryClient.setQueryData(['entity', id], data)
    },
  })

  const updateEntity = async (id: string, payload: EntityUpdatePayload): Promise<Entity> => {
    return updateEntityMutation.mutateAsync({ id, payload })
  }

  const getMemberAgreement = async (id: string): Promise<MemberAgreementResponse> => {
    return http.apiPost(`${ENDPOINT}/${id}/member_agreement`, {}) as Promise<MemberAgreementResponse>
  }

  const acceptAgreement = async (id: string): Promise<Entity> => {
    const updated = await http.apiPost(`${ENDPOINT}/${id}/accept_agreement`, {}) as Entity
    queryClient.setQueryData(['entity', id], updated)
    return updated
  }

  return {
    entity,
    updateEntity,
    getMemberAgreement,
    acceptAgreement,
  }
}
